import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { saveAs } from "file-saver";
import { getFileTypeExtension } from "../../../../../util/file-helpers";
import {
  GetDocPathNew,
  DownloadAllAttachmentsRegularZip,
  DownloadRegularCommentAttachment,
  GetRegularTaskComments,
  GetTaskRegularCommentsForAuditTrail,
} from "../../../../../services/api/task-api";
import CommentsTableNew from "../../tasks-comments/CommentsTable/CommentsTableNew";
import FileAttachmentListModalNew from "../../tasks-comments/FileAttachmentListModal/FileAttachmentListModalNew";
import DocumentViewerModalNew from "../../tasks-comments/DocumentViewerModal/DocumentViewerModalNew";
import Chat from "../../tasks-comments/ChatInterface/chat";
import { auditTrailSuggestions } from "../../tasks-comments/ChatInterface/chat-suggestion";
import "./tasks-comments-new.scss";

const TasksCommentsNew = ({
  assignID,
  type,
  dateFormatID,
  taskMembers,
  workItem,
  disabled,
  setTags,
  setAlertConfig,
  alertConfig,
  refreshCommentsTable,
  setFormModal,
  showForm = true,
  setShowForm,
  labels,
  ...props
}) => {
  const [comments, setComments] = useState([]); // List of comment data
  const [commentIndex, setCommentIndex] = useState([]); // List of indexes of comments that clip out of row
  const [currComment, setCurrComment] = useState({}); // Current comment row being acted upon
  const [commentAttachment, setCommentAttachment] = useState(0);
  const [docPathList, setDocPathList] = useState([]); // Stores the docpath for each file for currently viewed comment
  const [oneDoc, setOneDoc] = useState({}); // Stores the one file being viewed
  const [filesList, setFilesList] = useState(false); // Activates view comment modal
  const [showDocFileViewer, setShowDocFileViewer] = useState(false); // Activates Doc view comment modal
  const [commentViewer, setCommentViewer] = useState(false); // Allows view comment modal to only display comment

  useEffect(() => {
    refreshComments();
  }, [refreshCommentsTable]);

  const clipped = (index) =>
    setCommentIndex((prevIndexes) => [...prevIndexes, index]);

  const sortCommentsByDate = (comments) => {
    return comments.sort((a, b) => new Date(b.Created) - new Date(a.Created));
  };

  const refreshComments = async () => {
    try {
      let commentsResult = await GetTaskRegularCommentsForAuditTrail(assignID);
      setTags(taskMembers.map((elem) => elem.User.Name));
      if (commentsResult?.data.length > 0) {
        setComments(sortCommentsByDate(commentsResult.data));
        setCommentIndex([]);
      }
    } catch (error) {
      console.error("Error in refreshComments: ", error);
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Cannot get comments",
        alertMessage: error.toString(),
        alertVariant: "danger",
      }));
    }
  };

  const viewFiles = async (comment) => {
    let multipleDocs = false;
    let docIDList = { ids: comment.Files.map((doc) => doc.Id), isStandardTask: true };

    try {
      let docItem = {};
      let result = (await GetDocPathNew(docIDList)).data;

      if (!result) {
        return;
      } else if (result.length === 1) {
        docItem.docPath = result[0];
      } else {
        const docPathList = result.map((path) => path);
        setDocPathList(docPathList);
        multipleDocs = true;
      }

      setOneDoc(!multipleDocs ? docItem : {});
      setShowDocFileViewer(!multipleDocs);
      setCommentViewer(!multipleDocs);
      setFilesList(multipleDocs);
      setCurrComment(comment);
      setCommentAttachment(1);
    } catch (error) {
      console.error("Error in viewFiles:", error);
    }
  };

  const onDownloadingFiles = async (filesList, comment = null) => {
    const isMultipleFiles = Array.isArray(filesList);
    if (isMultipleFiles && filesList.length > 1) {
      await downloadMultipleFiles(comment.Id);
    } else if (isMultipleFiles && filesList.length === 1) {
      await downloadSingleFile(filesList[0]);
    } else {
      await downloadSingleFile(filesList);
    }
  };

  const getDownloadingEndpoint = async (fileId, fileContentType, fileName) => {
    let response = null;
    let downloadedFileType = getFileTypeExtension(fileName);
    let fileDownloadDetails = {
      Id: 0,
      DocumentId: fileId,
      AttachmentType: downloadedFileType,
      TaskId:
        (props.task?.Id || workItem?.assignID || assignID) ?? 0,
      Scroll: "0",
    };

    response = await DownloadRegularCommentAttachment(
      0,
      fileContentType,
      fileDownloadDetails
    );

    return response;
  };

  const downloadSingleFile = async (file) => {
    try {
      const result = await getDownloadingEndpoint(
        file.Id,
        file.FileContentType,
        file.Name
      );
      const blob = new Blob([result.data]);
      saveAs(blob, file.Name);
    } catch (error) {
      console.error("Error downloading file: ", error);
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Cannot Attach: ",
        alertMessage: "The file could not be downloaded",
        alertVariant: "danger",
      }));
    }
  };

  const downloadMultipleFiles = async (commentId) => {
    try {
      let date = new Date();
      let dateStr =
        "Attachments-" +
        date.getDate() +
        "_" +
        (date.getMonth() + 1) +
        "_" +
        date.getFullYear();
      let dateStrZip = dateStr + ".zip";

      let result = await DownloadAllAttachmentsRegularZip(commentId);

      const blob = new Blob([result.data]);
      saveAs(blob, dateStrZip);
    } catch (error) {
      console.error("Error downloading multiple files: ", error);
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Cannot Attach: ",
        alertMessage: "The file could not be downloaded",
        alertVariant: "danger",
      }));
    }
  };

  return (
    <div className="comments comment-table">
      <h4 className="heading mb-0">Audit Trail</h4>
      {showDocFileViewer && (
        <DocumentViewerModalNew
          showDocFileViewer={showDocFileViewer}
          setShowDocFileViewer={setShowDocFileViewer}
          commentAttachment={commentAttachment}
          setCommentAttachment={setCommentAttachment}
          oneDoc={oneDoc}
          currComment={currComment}
          commentViewer={commentViewer}
          setCommentViewer={setCommentViewer}
          onDownloadingFiles={onDownloadingFiles}
          task={props.task}
          workItem={workItem}
          type={type}
          assignID={assignID}
        />
      )}
      {filesList &&
        currComment.Action !== "Reminded" &&
        currComment.Note !== "" && (
          <FileAttachmentListModalNew
            filesList={filesList}
            setFilesList={setFilesList}
            currComment={currComment}
            docPathList={docPathList}
            setShowDocFileViewer={setShowDocFileViewer}
            setOneDoc={setOneDoc}
            setCommentViewer={setCommentViewer}
            setCommentAttachment={setCommentAttachment}
            projectTypeID={
              workItem?.projectTypeID ?? props.task?.projectTypeID
            }
            workItemType={
              workItem?.workItemType ?? props.task?.workItemType
            }
            onDownloadingFiles={onDownloadingFiles}
          />
        )}
      <div className="comments-list-container">
        {!showForm && (
          <Chat data={comments
            .map((item) => item.Note)
            .join("")
            .replaceAll("<No comments entered>", "")} 
            setShowForm={setShowForm} 
            suggestions={auditTrailSuggestions}/>
        )}
        <CommentsTableNew
          comments={comments}
          commentIndex={commentIndex}
          dateFormatID={dateFormatID}
          viewFiles={viewFiles}
          onDownloadingFiles={onDownloadingFiles}
          clipped={clipped}
          setFilesList={setFilesList}
          setCurrComment={setCurrComment}
          docuType={props.docuType}
          setFormModal={setFormModal}
          labels={labels}
        />
      </div>
    </div>
  );
};

TasksCommentsNew.propTypes = {
  type: PropTypes.string,
  workItem: PropTypes.object,
  task: PropTypes.object,
  dateFormatID: PropTypes.string,
  docuType: PropTypes.number,
  assignID: PropTypes.number,
  taskMembers: PropTypes.array,
  setTags: PropTypes.func,
  setAlertConfig: PropTypes.func,
  alertConfig: PropTypes.object,
  refreshCommentsTable: PropTypes.bool,
  setFormModal: PropTypes.func,
  showForm: PropTypes.bool,
  setShowForm: PropTypes.func,
  disabled: PropTypes.bool,
  labels: PropTypes.shape({
      ProjectSingular: PropTypes.string,
      ProjectPlural: PropTypes.string,
      ProjectSingularPossessive: PropTypes.string,
      ProjectPluralPossessive: PropTypes.string,
      TaskSingular: PropTypes.string,
      TaskPlural: PropTypes.string,
      TaskSingularPossessive: PropTypes.string,
      TaskPluralPossessive: PropTypes.string,
      FormSingular: PropTypes.string,
      FormPlural: PropTypes.string,
      FormSingularPossessive: PropTypes.string,
      FormPluralPossessive: PropTypes.string,
  }),
};
export default TasksCommentsNew;
